import React, { useState, useCallback } from 'react';

export interface NotificationItem {
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message?: string;
    duration?: number;
}

export const useNotification = () => {
    const [notifications, setNotifications] = useState<NotificationItem[]>([]);

    const showNotification = useCallback((
        type: 'success' | 'error' | 'warning' | 'info',
        title: string,
        message?: string,
        duration?: number
    ) => {
        const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
        const newNotification: NotificationItem = {
            id,
            type,
            title,
            message,
            duration: duration || 3000
        };

        setNotifications(prev => [...prev, newNotification]);

        // Auto-remove notification after duration
        setTimeout(() => {
            removeNotification(id);
        }, duration || 3000);
    }, []);

    const removeNotification = useCallback((id: string) => {
        setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, []);

    const showSuccess = useCallback((title: string, message?: string, duration?: number) => {
        showNotification('success', title, message, duration);
    }, [showNotification]);

    const showError = useCallback((title: string, message?: string, duration?: number) => {
        showNotification('error', title, message, duration);
    }, [showNotification]);

    const showWarning = useCallback((title: string, message?: string, duration?: number) => {
        showNotification('warning', title, message, duration);
    }, [showNotification]);

    const showInfo = useCallback((title: string, message?: string, duration?: number) => {
        showNotification('info', title, message, duration);
    }, [showNotification]);

    return {
        notifications,
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        removeNotification
    };
}; 